import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { Button, message, Spin, Typography, Layout } from "antd";
import { DesktopOutlined, ReloadOutlined, ExportOutlined } from "@ant-design/icons";
import AppSider from "../components/AppSider";
import { useTheiaAutoPause } from "../components/useTheiaAutoPause";

const { Content } = Layout;

export const IdePage = () => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const [theiaUrl, setTheiaUrl] = useState<string>("");
    const [isReady, setIsReady] = useState(false);

    useTheiaAutoPause(userId, true);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const folder = queryParams.get('folder');

    const fetchTheiaUrl = async () => {
        if (!userId) return;

        try {
            // Call backend to get/provision port - Use standard API URL from env
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';

            const urlParam = folder ? `&folder=${folder}` : '';
            const urlFetch = `${baseUrl}:8061/jutge/theia/port?id_usuari=${userId}${urlParam}`;
            console.log("Fetching Theia Url:", urlFetch);

            const token = localStorage.getItem('authToken');

            const response = await fetch(urlFetch, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 202) {
                // Provisioning in progress, poll again in 3s
                setTimeout(fetchTheiaUrl, 3000);
                return;
            }

            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") === -1) {
                const text = await response.text();
                console.error("Non-JSON Response received:", text);
                throw new Error("El servidor devolvió HTML en lugar de JSON. URL incorrecta o sesión expirada.");
            }

            if (!response.ok) throw new Error("Error connecting to backend");

            const data = await response.json();

            if (data.error) throw new Error(data.error);
            if (data.status === 'provisioning') {
                // Just in case status is 200 but body implies provisioning
                setTimeout(fetchTheiaUrl, 3000);
                return;
            }

            if (data.hostname === 'Pending...' || data.port === 0) {
                console.log("IDE Pending... Retrying in 20s");
                setTimeout(fetchTheiaUrl, 20000);
                return;
            }

            const hostname = data.hostname || window.location.hostname;
            // Force HTTP (Direct IP Access)
            let url = `http://${hostname}:${data.port}`;

            // Ensure we point to the correct workspace root
            if (!url.includes('#')) {
                // If folder is present, open that specific folder project
                const path = folder ? `/${folder}` : '';
                url = `${url}/#/root/projects${path}`;
            }

            console.log("Ideal URL:", url);
            setTheiaUrl(url);
            setIsReady(true);

        } catch (error) {
            console.error(error);
            message.error("Error: " + (error as any).message);
        }
    };

    useEffect(() => {
        setBreadCrumb([], true);
        if (userId) {
            fetchTheiaUrl();
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            // Bloquear Shift + Tabulador
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
            }
            // Bloquear tecla Alt (esto intercepta el inicio de Alt+Tab)
            if (e.key === 'Alt') {
                e.preventDefault();
            }
            // Bloquear F12 y Ctrl+Shift+I (Herramientas de desarrollador)
            if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
                e.preventDefault();
            }
        };

        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault(); // Bloquear click derecho
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('contextmenu', handleContextMenu);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [userId]);

    const setPnt = (value: number) => { };

    // Calculate menu index
    const isTeacher = user?.tipus === "0" || user?.tipus === "1";
    const menuIndex = isTeacher ? 7 : 6;

    return (
        <>
            <AppSider currentVal={menuIndex} setPantalla={setPnt} />
            <Content style={{ padding: '24px', background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
                <div style={{ background: '#fff', padding: 24, minHeight: 380, borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <Typography.Title level={2} style={{ margin: 0 }}>💻 Mi Entorno de Desarrollo</Typography.Title>
                        <Button icon={<ReloadOutlined />} onClick={fetchTheiaUrl} loading={!isReady && theiaUrl === ""}>
                            Recargar Estado
                        </Button>
                    </div>

                    {!isReady ? (
                        <div style={{ textAlign: 'center', padding: '50px 0' }}>
                            <Spin size="large" tip="Conectando con tu entorno..." />
                            <h3 style={{ marginTop: 20 }}>Preparando tu IDE personalizado...</h3>
                            <p>Esto puede tardar unos segundos mientras arranca el contenedor.</p>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <DesktopOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 24 }} />
                            <Typography.Title level={3}>🚀 Entorno de Desarrollo Listo</Typography.Title>
                            <Typography.Paragraph>
                                Tu IDE personal está ejecutándose en <b>{theiaUrl}</b>.
                            </Typography.Paragraph>
                            <Typography.Paragraph type="secondary">
                                Puedes abrirlo en una nueva pestaña para trabajar cómodamente a pantalla completa.
                            </Typography.Paragraph>
                            <Button type="primary" size="large" onClick={() => window.open(theiaUrl, '_blank')} icon={<ExportOutlined />}>
                                Abrir IDE Completo
                            </Button>
                        </div>
                    )}
                </div>
            </Content>
        </>
    );
};